'use client';

import {
  ReactNode,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';

import type { CSSVariable, Schemes } from '@personly/libs-types';
import { toRootCssVariables } from '@personly/libs-css';

import { useCookies } from './cookies.js';

type SchemesContextType = {
  schemeHandle: string;
  setSchemeHandle: (scheme: string) => void;
  cssVariables: Record<string, Array<string>>;
  displaySchemeHandle: string;
};

const setCssRootVariables = (cssVariables: Array<CSSVariable>) => {
  const rootCssVariables = toRootCssVariables(cssVariables);

  const root = document.querySelector(':root') as HTMLElement;

  for (const { key, value } of rootCssVariables) {
    root.style.setProperty(`--${key}`, value);
  }
};

const reduceCssRootVariables = (cssVariables: Array<CSSVariable>) =>
  cssVariables.reduce<Record<string, Array<string>>>(
    (acc, cur) => ({ ...acc, [cur.key]: cur.value }),
    {}
  );

const SchemesContext = createContext<SchemesContextType | null>(null);

interface Props {
  children: ReactNode;
  schemes: Schemes;
  cookieName?: string;
  hasWindow?: boolean;
}

const SchemesProvider = ({
  children,
  schemes,
  cookieName = 'schemeHandle',
  hasWindow = Boolean(typeof window !== 'undefined' && window),
}: Props) => {
  const { getCookie, setCookie } = useCookies();

  const [scheme, setScheme] = useState(schemes.scheme);
  const [cookieScheme, setCookieScheme] = useState<string | null>(null);

  const schemeValid = useCallback(
    (scheme: string) => schemes.schemes.map(({ id }) => id).includes(scheme),
    [schemes.schemes]
  );

  useEffect(() => {
    const initialCookieScheme = getCookie(cookieName);
    if (initialCookieScheme && schemeValid(initialCookieScheme)) {
      setCookieScheme(initialCookieScheme);
    }
  }, [schemeValid, getCookie, cookieName]);

  const updateScheme = useCallback(
    (scheme: string) => {
      if (scheme === 'SYSTEM') {
        const browserSchemes = schemes.schemes.filter(
          ({ flags }) => flags.browser
        );

        for (const browserScheme of browserSchemes) {
          const media = `(prefers-color-scheme: ${browserScheme.id.toLowerCase()})`;

          if (window.matchMedia(media).matches) {
            setCssRootVariables(browserScheme.cssVariables);
            setScheme(browserScheme.id);
            break;
          }
        }
      } else {
        const foundScheme = schemes.schemes.find(({ id }) => id === scheme);

        if (foundScheme) {
          const { cssVariables } = foundScheme;

          setCssRootVariables(cssVariables);
          setScheme(scheme);
        }
      }
    },
    [schemes.schemes]
  );

  const setSchemeHandle = useCallback(
    (scheme: string) => {
      if (schemeValid(scheme)) {
        setCookie('schemeHandle', scheme);
        setCookieScheme(scheme);
      }
    },
    [setCookie, schemeValid]
  );

  useEffect(() => {
    if (cookieScheme) updateScheme(cookieScheme);
  }, [cookieScheme, updateScheme]);

  const cssVariables = useMemo(() => {
    if (scheme === 'SYSTEM') {
      let cssVariables: Array<CSSVariable> = [];
      if (hasWindow) {
        const browserSchemes = schemes.schemes.filter(
          ({ flags }) => flags.browser
        );

        for (const browserScheme of browserSchemes) {
          const media = `(prefers-color-scheme: ${browserScheme.id.toLowerCase()})`;

          if (window.matchMedia(media).matches) {
            cssVariables = browserScheme.cssVariables;
            break;
          }
        }
      } else {
        const foundScheme = schemes.schemes.find(({ flags }) => flags.browser);
        if (foundScheme) {
          const { cssVariables } = foundScheme;

          return reduceCssRootVariables(cssVariables);
        }
      }

      return reduceCssRootVariables(cssVariables);
    } else {
      const foundScheme = schemes.schemes.find(({ id }) => id === scheme);

      if (foundScheme) {
        const { cssVariables } = foundScheme;

        return reduceCssRootVariables(cssVariables);
      }
    }
    /* c8 ignore next 2 */
    return {};
  }, [schemes, scheme, hasWindow]);

  const schemeHandle = useMemo(
    () => cookieScheme || scheme,
    [cookieScheme, scheme]
  );

  const displaySchemeHandle = useMemo(
    () => (schemeHandle === 'SYSTEM' ? schemes.schemes[0].id : schemeHandle),
    [schemeHandle, schemes.schemes]
  );

  const value = useMemo(
    () => ({
      schemeHandle,
      setSchemeHandle,
      cssVariables,
      displaySchemeHandle,
    }),
    [setSchemeHandle, schemeHandle, cssVariables, displaySchemeHandle]
  );

  return (
    <SchemesContext.Provider value={value}>{children}</SchemesContext.Provider>
  );
};

SchemesProvider.propTypes = {};

function useSchemes() {
  const context = useContext(SchemesContext);
  if (!context) throw new Error('useSchemes requires a context!');

  return context;
}

export { SchemesProvider, useSchemes };
